import { useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { CategoryDTO, ChannelDTO, EmojiDTO, MemberDTO, MemberProfileDTO, PublicUser, RoleDTO, ServerDTO } from '@clanfire/shared';
import { serverApi } from '@/api/endpoints';
import { useUsers, usePresence } from './users';

export type ServerSummary = Omit<ServerDTO, 'channels' | 'roles' | 'categories' | 'emojis'>;
type LoadState = 'loading' | 'loaded' | 'error';

interface ServersState {
  order: string[];
  servers: Record<string, ServerSummary>;
  channels: Record<string, ChannelDTO>;
  categories: Record<string, CategoryDTO>;
  roles: Record<string, RoleDTO>;
  /** Custom emoji of every server you're in, by id. */
  emojis: Record<string, EmojiDTO>;
  members: Record<string, Record<string, MemberDTO>>;
  membersState: Record<string, LoadState>;

  hydrate: (servers: ServerDTO[]) => void;
  upsertServer: (server: ServerDTO) => void;
  patchServer: (id: string, patch: Partial<ServerSummary>) => void;
  removeServer: (id: string) => void;
  upsertChannel: (channel: ChannelDTO) => void;
  removeChannel: (id: string) => void;
  touchChannel: (channelId: string, messageId: string | null, at: string | null) => void;
  upsertCategory: (category: CategoryDTO) => void;
  removeCategory: (categoryId: string) => void;
  /** Applies a bulk sidebar layout (positions + categories). */
  applyLayout: (layout: { channels: Array<{ id: string; position: number; categoryId: string | null }>; categories: Array<{ id: string; position: number }> }) => void;
  setOrder: (serverIds: string[]) => void;
  upsertRole: (role: RoleDTO) => void;
  removeRole: (roleId: string, serverId: string) => void;
  upsertEmoji: (emoji: EmojiDTO) => void;
  removeEmoji: (emojiId: string) => void;
  loadMembers: (serverId: string, force?: boolean) => Promise<void>;
  upsertMember: (member: MemberDTO) => void;
  removeMember: (serverId: string, userId: string) => void;
  reset: () => void;
}

function splitServer(server: ServerDTO) {
  const { channels, roles, categories, emojis, ...summary } = server;
  return { summary, channels, roles, categories, emojis };
}

const empty = { order: [], servers: {}, channels: {}, categories: {}, roles: {}, emojis: {}, members: {}, membersState: {} };

export const useServers = create<ServersState>((set, get) => ({
  ...empty,

  hydrate: (list) => {
    const servers: Record<string, ServerSummary> = {};
    const channels: Record<string, ChannelDTO> = {};
    const categories: Record<string, CategoryDTO> = {};
    const roles: Record<string, RoleDTO> = {};
    const emojis: Record<string, EmojiDTO> = {};
    for (const server of list) {
      const parts = splitServer(server);
      servers[server.id] = parts.summary;
      for (const c of parts.channels) channels[c.id] = c;
      for (const c of parts.categories) categories[c.id] = c;
      for (const r of parts.roles) roles[r.id] = r;
      for (const e of parts.emojis) emojis[e.id] = e;
    }
    set({ order: list.map((s) => s.id), servers, channels, categories, roles, emojis, members: {}, membersState: {} });
  },

  upsertServer: (server) =>
    set((state) => {
      const parts = splitServer(server);
      const channels = Object.fromEntries(Object.entries(state.channels).filter(([, c]) => c.serverId !== server.id));
      const roles = Object.fromEntries(Object.entries(state.roles).filter(([, r]) => r.serverId !== server.id));
      const categories = Object.fromEntries(Object.entries(state.categories).filter(([, c]) => c.serverId !== server.id));
      const emojis = Object.fromEntries(Object.entries(state.emojis).filter(([, e]) => e.serverId !== server.id));
      for (const c of parts.channels) channels[c.id] = c;
      for (const c of parts.categories) categories[c.id] = c;
      for (const r of parts.roles) roles[r.id] = r;
      for (const e of parts.emojis) emojis[e.id] = e;
      return {
        order: state.order.includes(server.id) ? state.order : [...state.order, server.id],
        servers: { ...state.servers, [server.id]: parts.summary },
        channels,
        categories,
        roles,
        emojis,
      };
    }),

  patchServer: (id, patch) =>
    set((state) => (state.servers[id] ? { servers: { ...state.servers, [id]: { ...state.servers[id]!, ...patch } } } : state)),

  removeServer: (id) =>
    set((state) => {
      const { [id]: _removed, ...servers } = state.servers;
      const { [id]: _members, ...members } = state.members;
      const { [id]: _ms, ...membersState } = state.membersState;
      return {
        order: state.order.filter((s) => s !== id),
        servers,
        members,
        membersState,
        channels: Object.fromEntries(Object.entries(state.channels).filter(([, c]) => c.serverId !== id)),
        categories: Object.fromEntries(Object.entries(state.categories).filter(([, c]) => c.serverId !== id)),
        roles: Object.fromEntries(Object.entries(state.roles).filter(([, r]) => r.serverId !== id)),
        emojis: Object.fromEntries(Object.entries(state.emojis).filter(([, e]) => e.serverId !== id)),
      };
    }),

  upsertChannel: (channel) => set((state) => ({ channels: { ...state.channels, [channel.id]: channel } })),

  removeChannel: (id) =>
    set((state) => {
      const { [id]: _removed, ...channels } = state.channels;
      return { channels };
    }),

  touchChannel: (channelId, messageId, at) =>
    set((state) => {
      const channel = state.channels[channelId];
      if (!channel) return state;
      return { channels: { ...state.channels, [channelId]: { ...channel, lastMessageId: messageId, lastMessageAt: at } } };
    }),

  upsertCategory: (category) => set((state) => ({ categories: { ...state.categories, [category.id]: category } })),

  removeCategory: (categoryId) =>
    set((state) => {
      const { [categoryId]: _removed, ...categories } = state.categories;
      // Channels of a deleted category become uncategorized (mirrors ON DELETE SET NULL).
      const channels = Object.fromEntries(
        Object.entries(state.channels).map(([id, c]) => [id, c.categoryId === categoryId ? { ...c, categoryId: null } : c]),
      );
      return { categories, channels };
    }),

  applyLayout: (layout) =>
    set((state) => {
      const channels = { ...state.channels };
      for (const c of layout.channels) if (channels[c.id]) channels[c.id] = { ...channels[c.id]!, position: c.position, categoryId: c.categoryId };
      const categories = { ...state.categories };
      for (const c of layout.categories) if (categories[c.id]) categories[c.id] = { ...categories[c.id]!, position: c.position };
      return { channels, categories };
    }),

  setOrder: (serverIds) =>
    set((state) => {
      const known = serverIds.filter((id) => state.servers[id]);
      return { order: [...known, ...state.order.filter((id) => !known.includes(id))] };
    }),

  upsertRole: (role) => set((state) => ({ roles: { ...state.roles, [role.id]: role } })),

  upsertEmoji: (emoji) => set((state) => ({ emojis: { ...state.emojis, [emoji.id]: emoji } })),

  removeEmoji: (emojiId) =>
    set((state) => {
      const { [emojiId]: _removed, ...emojis } = state.emojis;
      return { emojis };
    }),

  removeRole: (roleId, serverId) =>
    set((state) => {
      const { [roleId]: _removed, ...roles } = state.roles;
      // Mirror the server: members lose a deleted role.
      const without = <T extends { roleIds: string[] }>(m: T): T => (m.roleIds.includes(roleId) ? { ...m, roleIds: m.roleIds.filter((id) => id !== roleId) } : m);
      const serverMembers = state.members[serverId];
      const members = serverMembers
        ? { ...state.members, [serverId]: Object.fromEntries(Object.entries(serverMembers).map(([uid, m]) => [uid, without(m)])) }
        : state.members;
      const server = state.servers[serverId];
      const me = server && without(server.me);
      const servers = server && me !== server.me ? { ...state.servers, [serverId]: { ...server, me } } : state.servers;
      return { roles, members, servers };
    }),

  loadMembers: async (serverId, force = false) => {
    const current = get().membersState[serverId];
    if (!force && (current === 'loading' || current === 'loaded')) return;
    set((s) => ({ membersState: { ...s.membersState, [serverId]: 'loading' } }));
    const requestedAt = Date.now();
    try {
      const list = await serverApi.members(serverId);
      useUsers.getState().upsert(list.map((m) => m.user));
      usePresence.getState().mergeSnapshot(Object.fromEntries(list.map((m) => [m.userId, m.presence])), requestedAt);
      set((s) => ({
        members: { ...s.members, [serverId]: Object.fromEntries(list.map((m) => [m.userId, m])) },
        membersState: { ...s.membersState, [serverId]: 'loaded' },
      }));
    } catch {
      set((s) => ({ membersState: { ...s.membersState, [serverId]: 'error' } }));
    }
  },

  upsertMember: (member) =>
    set((state) => {
      useUsers.getState().upsert([member.user]);
      const serverMembers = state.members[member.serverId];
      // Only track members for servers whose list has been loaded.
      if (!serverMembers) return state;
      return { members: { ...state.members, [member.serverId]: { ...serverMembers, [member.userId]: member } } };
    }),

  removeMember: (serverId, userId) =>
    set((state) => {
      const serverMembers = state.members[serverId];
      if (!serverMembers?.[userId]) return state;
      const { [userId]: _removed, ...rest } = serverMembers;
      return { members: { ...state.members, [serverId]: rest } };
    }),

  reset: () => set(empty),
}));

const byPosition = (a: { position: number; createdAt?: string }, b: { position: number; createdAt?: string }) =>
  a.position - b.position || (a.createdAt ?? '').localeCompare(b.createdAt ?? '');

export function useServerChannels(serverId: string | undefined): ChannelDTO[] {
  const channels = useServers(useShallow((s) => Object.values(s.channels).filter((c) => c.serverId === serverId)));
  return [...channels].sort(byPosition);
}

export function useServerRoles(serverId: string | undefined): RoleDTO[] {
  const roles = useServers(useShallow((s) => Object.values(s.roles).filter((r) => r.serverId === serverId)));
  return [...roles].sort((a, b) => b.position - a.position);
}

/** Roles (other than the default role) from a list of ids, highest first. */
export function rolesFrom(roles: Record<string, RoleDTO>, roleIds: readonly string[] | undefined): RoleDTO[] {
  return (roleIds ?? [])
    .map((id) => roles[id])
    .filter((r): r is RoleDTO => !!r)
    .sort((a, b) => b.position - a.position);
}

/** A member's roles other than the default role (which everyone has), highest first. */
export function useMemberRoles(serverId: string | null | undefined, userId: string | null | undefined): RoleDTO[] {
  return useServers(useShallow((s) => (serverId && userId ? rolesFrom(s.roles, s.members[serverId]?.[userId]?.roleIds) : [])));
}

/** The colour a member's name shows in: that of their highest role with a colour. */
export function roleColor(roles: readonly RoleDTO[]): string | undefined {
  return roles.find((r) => r.color)?.color ?? undefined;
}

/** A server's custom emoji, by name. */
export function useServerEmojis(serverId: string | null | undefined): EmojiDTO[] {
  const emojis = useServers(useShallow((s) => Object.values(s.emojis).filter((e) => e.serverId === serverId)));
  return [...emojis].sort((a, b) => a.name.localeCompare(b.name));
}

export function useServerMembers(serverId: string | undefined): MemberDTO[] {
  return useServers(useShallow((s) => (serverId ? Object.values(s.members[serverId] ?? {}) : [])));
}

export function useServerCategories(serverId: string | undefined): CategoryDTO[] {
  const categories = useServers(useShallow((s) => Object.values(s.categories).filter((c) => c.serverId === serverId)));
  return [...categories].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

/** A member's name in a server: their nickname there, else their display name. */
export function memberName(serverId: string | null | undefined, userId: string | null | undefined): string | undefined {
  if (!userId) return undefined;
  const nickname = serverId ? useServers.getState().members[serverId]?.[userId]?.nickname : null;
  return nickname || useUsers.getState().users[userId]?.displayName;
}

/** Someone's profile just for this server (a Blaze perk), while they have Blaze. */
export function useMemberProfile(serverId: string | null | undefined, userId: string | null | undefined): MemberProfileDTO | null {
  const profile = useServers((s) => (serverId && userId ? (s.members[serverId]?.[userId]?.profile ?? null) : null));
  const blaze = useUsers((s) => (userId ? !!s.users[userId]?.blazeSince : false));
  return blaze ? profile : null;
}

type Lookable = Pick<PublicUser, 'avatarUrl'> & Partial<Pick<PublicUser, 'bannerUrl' | 'bio'>>;

/** A user with their server profile's avatar, banner and bio (where they've set them) in place of their own. */
export function withServerProfile<T extends Lookable>(user: T, profile: MemberProfileDTO | null): T {
  if (!profile) return user;
  return {
    ...user,
    ...(profile.avatarUrl && { avatarUrl: profile.avatarUrl }),
    ...(profile.bannerUrl && 'bannerUrl' in user && { bannerUrl: profile.bannerUrl }),
    ...(profile.bio && 'bio' in user && { bio: profile.bio }),
  };
}

/** How a user looks in a server (see withServerProfile). */
export function useServerLook<T extends (Lookable & { id: string }) | null | undefined>(serverId: string | null | undefined, user: T): T {
  const profile = useMemberProfile(serverId, user?.id);
  return useMemo(() => (user && profile ? withServerProfile(user, profile) : user), [user, profile]);
}

export function useMemberName(serverId: string | null | undefined, userId: string | null | undefined): string | undefined {
  const nickname = useServers((s) => (serverId && userId ? s.members[serverId]?.[userId]?.nickname : null));
  const displayName = useUsers((s) => (userId ? s.users[userId]?.displayName : undefined));
  return nickname || displayName;
}
